import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import apiClient from '../../services/apiClient'
import AdminRichTextEditor from '../admin/AdminRichTextEditor'

export type CaseStudyRecord = {
  id?: number
  name?: string
  businessProblem?: string
  domain?: string
  updatedAt?: string
} | null

type Props = {
  courseName?: string | null
  initial?: CaseStudyRecord
  onSaved?: (record: CaseStudyRecord) => void
}

function isEmptyHtml(value: string) {
  return value.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim() === ''
}

export default function StudentCaseStudyForm({ courseName, initial, onSaved }: Props) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    businessProblem: initial?.businessProblem || '',
    domain: initial?.domain || '',
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<CaseStudyRecord>(initial || null)
  const [expanded, setExpanded] = useState(() => !initial)

  useEffect(() => {
    setForm({
      name: initial?.name || '',
      businessProblem: initial?.businessProblem || '',
      domain: initial?.domain || '',
    })
    setSaved(initial || null)
    // Đã đăng ký → mặc định thu hẹp
    setExpanded(!initial)
  }, [initial?.id, initial?.name, initial?.businessProblem, initial?.domain, initial?.updatedAt])

  const save = async () => {
    if (!form.name.trim() || isEmptyHtml(form.businessProblem) || !form.domain.trim()) {
      toast.error('Điền đủ tên case study, vấn đề DN và domain')
      return
    }
    try {
      setSaving(true)
      const res = await apiClient.put('/student/case-study', form)
      const record = res.data?.data || null
      setSaved(record)
      toast.success(saved ? 'Đã cập nhật case study' : 'Đã đăng ký case study')
      setExpanded(false)
      onSaved?.(record)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Lưu thất bại')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="rounded-xl border border-amber-300 bg-amber-50/80 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="min-w-0">
          <h2 className="font-semibold text-amber-950 text-base">
            Case Study
            {courseName ? ` — ${courseName}` : ' (BAAIONL)'}
          </h2>
          {saved ? (
            <p className="text-xs text-amber-900/80 mt-0.5 truncate">
              Đã đăng ký: <strong>{saved.name}</strong>
              {saved.domain ? ` · ${saved.domain}` : ''}
            </p>
          ) : (
            <p className="text-xs text-amber-900 mt-0.5">
              Chưa đăng ký — bắt buộc trước khi làm BTVN
            </p>
          )}
        </div>
        <span className="text-amber-900 text-sm shrink-0">{expanded ? 'Thu gọn ▴' : 'Mở rộng ▾'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-amber-200/80 pt-3">
          {!saved && (
            <p className="text-sm text-amber-900 mb-3">
              Bạn chưa đăng ký case study. Hãy đăng ký trước khi làm BTVN.
            </p>
          )}
          <div className="rounded-lg border border-amber-200 bg-white/80 p-3 text-sm text-amber-950 mb-4 space-y-1">
            <p className="font-medium">Hướng dẫn quan trọng</p>
            <p>
              Chọn <strong>một</strong> case study và thực hiện <strong>từ đầu</strong> (phân tích /
              thiết kế / đề xuất giải pháp mới). Không chỉnh sửa hay vá một hệ thống đang chạy sẵn.
            </p>
          </div>

          <div className="grid gap-3">
            <label className="block text-sm">
              <span className="font-medium">Tên case study</span>
              <input
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="VD: Hệ thống quản lý đơn hàng cho SME bán lẻ"
              />
            </label>
            <div className="block text-sm">
              <span className="font-medium">Vấn đề của doanh nghiệp (HTML)</span>
              <div className="mt-1 bg-white rounded-md border border-m2-outline-variant overflow-hidden">
                <AdminRichTextEditor
                  value={form.businessProblem}
                  onChange={(html) => setForm((f) => ({ ...f, businessProblem: html }))}
                  placeholder="Mô tả pain point / mục tiêu nghiệp vụ cần giải quyết..."
                />
              </div>
            </div>
            <label className="block text-sm">
              <span className="font-medium">Domain</span>
              <input
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
                value={form.domain}
                onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                placeholder="VD: Retail, Banking, Logistics, Education..."
              />
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={() => void save()}
                className="px-4 py-2 rounded-md bg-m2-primary text-white text-sm"
              >
                {saving ? 'Đang lưu...' : saved ? 'Cập nhật case study' : 'Đăng ký case study'}
              </button>
              {saved?.updatedAt && (
                <span className="text-xs text-amber-900/70">
                  Cập nhật lần cuối: {new Date(saved.updatedAt).toLocaleString('vi-VN')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
